import React from 'react';
import { FileText, Mail, BookOpen, Bell, Tag, X } from 'lucide-react';

const TEMPLATES = [
  {
    id: 'welcome',
    label: 'Welcome',
    icon: Mail,
    color: 'success',
    description: 'Greet new members and introduce the platform',
    subject: 'Welcome to Fortified Mind! 🎉',
    body: `<p>Dear Fortified Mind family,</p>

<p>Welcome! We're so glad you've joined our community of believers committed to strengthening their faith through God's Word.</p>

<h2>Getting Started</h2>
<p>Here's what you can do right away:</p>
<ul>
  <li><strong>Add your first verse</strong> to your personal library</li>
  <li><strong>Start a reading plan</strong> to study the Bible systematically</li>
  <li><strong>Practice memorization</strong> with our spaced repetition system</li>
  <li><strong>Explore commentaries</strong> including SDA and Ellen G. White insights</li>
</ul>

<blockquote style="border-left:4px solid #f59e0b;margin:16px 0;padding:12px 20px;background:#fef9ee;color:#1a1a2e;font-style:italic;">"Your word is a lamp to my feet and a light to my path." — Psalm 119:105</blockquote>

<p style="text-align:center;"><a href="https://fortifiedmind.org/dashboard" style="display:inline-block;background:#f59e0b;color:#0f0f1a;padding:12px 28px;border-radius:8px;text-decoration:none;font-weight:600;">Go to Your Dashboard</a></p>

<p>If you have any questions, reply to this email or visit our <a href="https://fortifiedmind.org/contact-us" style="color:#f59e0b;">Contact page</a>.</p>

<p>Blessings,<br><strong>The Fortified Mind Team</strong></p>`,
  },
  {
    id: 'weekly-digest',
    label: 'Weekly Digest',
    icon: BookOpen,
    color: 'primary',
    description: 'Weekly summary of community highlights and tips',
    subject: 'Your Weekly Fortified Mind Digest 📖',
    body: `<h2>This Week in Fortified Mind</h2>
<p>Here's your weekly roundup of community highlights, featured verses, and study tips.</p>

<hr style="border:none;border-top:1px solid #e5e7eb;margin:24px 0;" />

<h3>✨ Featured Verse of the Week</h3>
<blockquote style="border-left:4px solid #f59e0b;margin:16px 0;padding:12px 20px;background:#fef9ee;color:#1a1a2e;font-style:italic;">"I can do all things through Christ who strengthens me." — Philippians 4:13</blockquote>

<h3>📚 Study Tip of the Week</h3>
<p>Try the <strong>chunking method</strong> — break long verses into 3–5 word phrases and memorize one chunk at a time before combining them. This dramatically improves retention!</p>

<h3>🏆 Community Highlights</h3>
<p>This week our community collectively memorized hundreds of verses. Keep up the incredible work!</p>

<hr style="border:none;border-top:1px solid #e5e7eb;margin:24px 0;" />

<p style="text-align:center;"><a href="https://fortifiedmind.org/dashboard" style="display:inline-block;background:#f59e0b;color:#0f0f1a;padding:12px 28px;border-radius:8px;text-decoration:none;font-weight:600;">Continue Your Study</a></p>

<p>Blessings,<br><strong>The Fortified Mind Team</strong></p>`,
  },
  {
    id: 'study-reminder',label: 'Study Reminder',icon: Bell,color: 'warning',description: 'Encourage members to continue their study streak',
    subject: "Don\'t Break Your Streak! 🔥 Your Daily Review Awaits",
    body: `<p>Hi there,</p>

<p>We noticed you haven't completed your daily review yet today. Your streak is worth protecting!</p>

<div style="background:#fef3c7;border:1px solid #f59e0b;border-radius:8px;padding:16px 20px;margin:16px 0;">
  <p style="margin:0;font-weight:600;color:#92400e;">📌 Your Daily Review is Ready</p>
  <p style="margin:8px 0 0;">Just a few minutes of review keeps your memorization sharp and your streak alive.</p>
</div>

<blockquote style="border-left:4px solid #f59e0b;margin:16px 0;padding:12px 20px;background:#fef9ee;color:#1a1a2e;font-style:italic;">"But his delight is in the law of the LORD, and on his law he meditates day and night." — Psalm 1:2</blockquote>

<h3>Why Daily Review Matters</h3>
<ul>
  <li>Spaced repetition strengthens long-term memory</li>
  <li>Even 5 minutes a day compounds into lasting knowledge</li>
  <li>Consistent review builds spiritual discipline</li>
</ul>

<p style="text-align:center;"><a href="https://fortifiedmind.org/daily-review-system" style="display:inline-block;background:#f59e0b;color:#0f0f1a;padding:12px 28px;border-radius:8px;text-decoration:none;font-weight:600;">Start My Review Now</a></p>

<p>Keep going — you're doing great!<br><strong>The Fortified Mind Team</strong></p>`,
  },
  {
    id: 'announcement',label: 'Announcement',icon: Bell,color: 'accent',description: 'Share important news or platform updates',subject: '📢 Important Announcement from Fortified Mind',
    body: `<h2>Important Announcement</h2>
<p>Dear Fortified Mind community,</p>

<p>We have an exciting update to share with you!</p>

<div style="background:#fef3c7;border:1px solid #f59e0b;border-radius:8px;padding:16px 20px;margin:16px 0;">
  <p style="margin:0;font-weight:600;color:#92400e;">📌 What's New</p>
  <p style="margin:8px 0 0;">[Describe your announcement here — new feature, event, policy update, etc.]</p>
</div>

<h3>What This Means for You</h3>
<p>[Explain the impact or benefit to members]</p>

<h3>What You Need to Do</h3>
<ul>
  <li>[Action item 1]</li>
  <li>[Action item 2]</li>
  <li>[Action item 3]</li>
</ul>

<p>If you have questions, please don't hesitate to <a href="https://fortifiedmind.org/contact-us" style="color:#f59e0b;">reach out to us</a>.</p>

<p style="text-align:center;"><a href="https://fortifiedmind.org" style="display:inline-block;background:#f59e0b;color:#0f0f1a;padding:12px 28px;border-radius:8px;text-decoration:none;font-weight:600;">Learn More</a></p>

<p>Thank you for being part of our community.<br><strong>The Fortified Mind Team</strong></p>`,
  },
  {
    id: 'promotional',label: 'Promotional',icon: Tag,color: 'destructive',description: 'Highlight features, plans, or special content',subject: '🌟 Discover What You\'re Missing on Fortified Mind',
    body: `<h2>Unlock the Full Power of Scripture Study</h2>
<p>Dear Fortified Mind member,</p>

<p>Did you know there are powerful features waiting for you to explore? Here's what you might be missing:</p>

<table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;margin:16px 0;">
  <tr>
    <td width="50%" style="padding:8px;vertical-align:top;">
      <div style="background:#fef9ee;border-radius:8px;padding:16px;">
        <p style="margin:0 0 8px;font-weight:600;color:#f59e0b;">📖 EGW Reading Plans</p>
        <p style="margin:0;font-size:14px;color:#374151;">Study Ellen G. White's writings with structured daily plans and audio support.</p>
      </div>
    </td>
    <td width="50%" style="padding:8px;vertical-align:top;">
      <div style="background:#fef9ee;border-radius:8px;padding:16px;">
        <p style="margin:0 0 8px;font-weight:600;color:#f59e0b;">🎙️ Neural TTS Audio</p>
        <p style="margin:0;font-size:14px;color:#374151;">Listen to Scripture with high-quality AI voices while you study or commute.</p>
      </div>
    </td>
  </tr>
  <tr>
    <td width="50%" style="padding:8px;vertical-align:top;">
      <div style="background:#fef9ee;border-radius:8px;padding:16px;">
        <p style="margin:0 0 8px;font-weight:600;color:#f59e0b;">🏆 Awards & Talents</p>
        <p style="margin:0;font-size:14px;color:#374151;">Earn recognition for your memorization milestones and spiritual growth.</p>
      </div>
    </td>
    <td width="50%" style="padding:8px;vertical-align:top;">
      <div style="background:#fef9ee;border-radius:8px;padding:16px;">
        <p style="margin:0 0 8px;font-weight:600;color:#f59e0b;">📊 Study Analytics</p>
        <p style="margin:0;font-size:14px;color:#374151;">Track your progress with detailed charts and memorization statistics.</p>
      </div>
    </td>
  </tr>
</table>

<blockquote style="border-left:4px solid #f59e0b;margin:16px 0;padding:12px 20px;background:#fef9ee;color:#1a1a2e;font-style:italic;">"Study to show yourself approved unto God, a workman that needs not to be ashamed, rightly dividing the word of truth." — 2 Timothy 2:15</blockquote>

<p style="text-align:center;"><a href="https://fortifiedmind.org/dashboard" style="display:inline-block;background:#f59e0b;color:#0f0f1a;padding:12px 28px;border-radius:8px;text-decoration:none;font-weight:600;">Explore All Features</a></p>

<p>Blessings,<br><strong>The Fortified Mind Team</strong></p>`,
  },
];

const colorMap = {
  success: 'bg-success/10 border-success/30 text-success',
  primary: 'bg-primary/10 border-primary/30 text-primary',
  warning: 'bg-warning/10 border-warning/30 text-warning',
  accent: 'bg-accent/10 border-accent/30 text-accent',
  destructive: 'bg-destructive/10 border-destructive/30 text-destructive',
};

const NewsletterTemplates = ({ onSelect, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="h-9 w-9 bg-primary/10 rounded-full flex items-center justify-center">
              <FileText className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">Newsletter Templates</h2>
              <p className="text-xs text-muted-foreground">Select a template to pre-fill your composer</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Templates list */}
        <div className="overflow-y-auto p-5 space-y-3 flex-1">
          {TEMPLATES?.map(template => {
            const IconComponent = template?.icon;
            const colorClass = colorMap?.[template?.color] || colorMap?.primary;
            return (
              <button
                key={template?.id}
                onClick={() => onSelect(template)}
                className="w-full text-left p-4 border border-border rounded-xl hover:border-primary/40 hover:bg-muted/30 transition-all group"
              >
                <div className="flex items-start space-x-4">
                  <div className={`p-2.5 rounded-lg border ${colorClass} shrink-0`}>
                    <IconComponent className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-semibold text-foreground group-hover:text-primary transition-colors">{template?.label}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${colorClass}`}>{template?.id}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{template?.description}</p>
                    <p className="text-xs text-muted-foreground/70 truncate">
                      <span className="font-medium">Subject:</span> {template?.subject}
                    </p>
                  </div>
                  <div className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-xs text-primary font-medium bg-primary/10 px-2 py-1 rounded">Use →</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="p-4 border-t border-border bg-muted/20 rounded-b-2xl">
          <p className="text-xs text-muted-foreground text-center">Templates pre-fill the subject and body — you can customize everything before sending.</p>
        </div>
      </div>
    </div>
  );
};

export default NewsletterTemplates;
